import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

// Dialog-only CSS starts loading with the component, without blocking the page's
// first paint. Share each load across dialogs and section re-renders.
/** @type {Map<string, Promise<void>>} */
const dialogStylesheets = new Map();

/** @param {string} url */
export function loadDialogStylesheet(url) {
  const href = new URL(url, document.baseURI).href;
  const existing = dialogStylesheets.get(href);
  if (existing) return existing;

  // A parser-discovered, non-matching media link can download early at low
  // priority on mobile. Apply it only after loading, without starting a second request.
  const existingLink = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
    (element) => element instanceof HTMLLinkElement && element.href === href
  );
  const link = existingLink instanceof HTMLLinkElement ? existingLink : document.createElement('link');
  if (link.sheet) {
    link.media = 'all';
    const ready = Promise.resolve();
    dialogStylesheets.set(href, ready);
    return ready;
  }
  if (!existingLink) {
    link.rel = 'stylesheet';
    link.href = href;
    link.fetchPriority = 'low';
  }

  /** @type {Promise<void>} */
  const ready = new Promise((resolve, reject) => {
    const cleanup = () => {
      link.removeEventListener('load', loaded);
      link.removeEventListener('error', failed);
    };
    const loaded = () => {
      cleanup();
      link.media = 'all';
      resolve();
    };
    const failed = () => {
      cleanup();
      link.remove();
      dialogStylesheets.delete(href);
      reject(new Error(`Unable to load dialog styles: ${href}`));
    };
    link.addEventListener('load', loaded);
    link.addEventListener('error', failed);
  });

  dialogStylesheets.set(href, ready);
  if (!existingLink) document.head.append(link);
  return ready;
}

/** Styles shared by components that must be complete before their first open. */
export class ComponentStyles {
  #host;
  #key = '';
  #ready = true;
  /** @type {Promise<void[]> | null} */
  #promise = null;

  /** @param {HTMLElement} host */
  constructor(host) {
    this.#host = host;
  }

  get ready() {
    return this.#ready && this.#key === (this.#host.dataset.componentStyles || '');
  }

  load() {
    const key = this.#host.dataset.componentStyles || '';
    if (key !== this.#key || !this.#promise) {
      this.#key = key;
      this.#ready = !key;
      const promise = Promise.all(key.split(/\s+/).filter(Boolean).map(loadDialogStylesheet));
      this.#promise = promise;
      promise.then(() => {
        if (this.#promise === promise) this.#ready = true;
      }, () => {
        if (this.#promise === promise) this.#promise = null;
      });
    }
    return this.#promise;
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #stylesKey = '';
  #stylesReady = true;
  /** @type {Promise<void[]> | null} */
  #stylesPromise = null;
  /** @type {AbortController | null} */
  #pendingOpen = null;

  #warmStyles() {
    const key = this.dataset.dialogStyles || '';
    if (key !== this.#stylesKey || !this.#stylesPromise) {
      this.#stylesKey = key;
      this.#stylesReady = !key;
      const promise = Promise.all(key.split(/\s+/).filter(Boolean).map(loadDialogStylesheet));
      this.#stylesPromise = promise;
      promise.then(() => {
        if (this.#stylesPromise === promise) this.#stylesReady = true;
      }, () => {
        // Permit a subsequent click to retry a failed resource.
        if (this.#stylesPromise === promise) this.#stylesPromise = null;
      });
    }
    return this.#stylesPromise;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#warmStyles();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#pendingOpen?.abort();
    this.#warmStyles();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#pendingOpen?.abort();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    if (!this.#stylesReady) {
      this.#pendingOpen?.abort();
      const pending = new AbortController();
      this.#pendingOpen = pending;
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') pending.abort();
        },
        { signal: pending.signal }
      );
      this.#warmStyles().then(() => {
        if (!pending.signal.aborted && this.isConnected && this.refs.dialog === dialog) {
          this.showDialog();
        }
      }, (error) => {
        if (!pending.signal.aborted) console.error(error);
      }).finally(() => {
        pending.abort();
        if (this.#pendingOpen === pending) this.#pendingOpen = null;
      });
      return;
    }

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (!this.isConnected || this.refs.dialog !== dialog || dialog.open) return;
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    this.#pendingOpen?.abort();
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
